<template>
  <footer class="bg-slate-900 text-slate-300 pt-16 pb-8 border-t border-slate-800">
    <div class="container mx-auto px-4">
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mb-12">
        <!-- Brand Info -->
        <div class="lg:col-span-2 space-y-6">
          <router-link to="/" class="flex items-center space-x-2">
            <img v-if="settings.logo" :src="settings.logo" alt="Logo" class="w-10 h-10 object-contain" />
            <div v-else class="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <span class="text-white font-bold text-xl">{{ settings.companyName ? settings.companyName.charAt(0) : 'K' }}</span>
            </div>
            <span class="text-2xl font-bold tracking-tight text-white">{{ settings.companyName }}</span>
          </router-link>
          <p class="max-w-md text-slate-400 leading-relaxed">{{ settings.footerDescription }}</p>
          <div class="flex space-x-4">
            <a href="#" class="hover:text-blue-500 transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
            </a>
            <a href="#" class="hover:text-blue-400 transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
            </a>
            <a href="#" class="hover:text-pink-500 transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
            </a>
            <a href="#" class="hover:text-blue-700 transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
            </a>
          </div>
        </div>

        <!-- Link sections -->
        <div v-for="section in footerLinks" :key="section.title" class="space-y-6">
          <h3 class="text-white font-bold uppercase tracking-wider text-sm">{{ section.title }}</h3>
          <ul class="space-y-4">
            <li v-for="link in section.links" :key="link.name">
              <router-link :to="link.href" class="hover:text-white transition-colors">{{ link.name }}</router-link>
            </li>
          </ul>
        </div>

        <!-- Contact Info -->
        <div class="space-y-6">
          <h3 class="text-white font-bold uppercase tracking-wider text-sm">联系方式</h3>
          <ul class="space-y-4">
            <li class="flex items-start space-x-3">
              <svg class="text-blue-500 shrink-0 mt-0.5" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
              <span>{{ settings.address }}</span>
            </li>
            <li class="flex items-center space-x-3">
              <svg class="text-blue-500 shrink-0" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
              <span>{{ settings.phone }}</span>
            </li>
            <li class="flex items-center space-x-3">
              <svg class="text-blue-500 shrink-0" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
              <span>{{ settings.email }}</span>
            </li>
          </ul>
        </div>
      </div>

      <!-- Bottom bar -->
      <div class="pt-8 border-t border-slate-800 text-sm flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0 text-slate-500">
        <p>© 2024 {{ settings.companyName }}科技股份有限公司. 版权所有. 京ICP备12345678号-1</p>
        <div class="flex space-x-6">
          <a href="#" class="hover:text-slate-300 transition-colors">隐私权政策</a>
          <a href="#" class="hover:text-slate-300 transition-colors">使用条款</a>
          <a href="#" class="hover:text-slate-300 transition-colors">Cookie 设置</a>
        </div>
      </div>
    </div>
  </footer>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'AppFooter',
  data() {
    return {
      footerLinks: [
        {
          title: '公司',
          links: [
            { name: '关于我们', href: '/about' },
            { name: '加入我们', href: '/about' },
            { name: '联系我们', href: '/contact' },
            { name: '新闻资讯', href: '/news' }
          ]
        },
        {
          title: '产品',
          links: [
            { name: '应用端口', href: '/products' },
            { name: '知识产权', href: '/products' },
            { name: '其他信息技术服务', href: '/products' },
            { name: '智能硬件', href: '/products' }
          ]
        },
        {
          title: '支持',
          links: [
            { name: '常见问题', href: '/contact' },
            { name: '技术支持', href: '/contact' },
            { name: '隐私政策', href: '/contact' },
            { name: '服务条款', href: '/contact' }
          ]
        }
      ]
    }
  },
  computed: {
    ...mapGetters(['siteSettings']),
    settings() { return this.siteSettings }
  }
}
</script>
